import PropTypes from 'prop-types';
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Dropdown, MenuItem } from 'react-bootstrap';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckLine,
  ClipboardCheck,
  Download,
  Ellipsis,
  Heart,
  HeartMinus,
  Star,
  X,
} from 'lucide-react';

import ApplicantSummaryCard from 'components/ClientATS/ApplicantSummaryCard';
import Markdown from 'components/Shared/Markdown';
import PortfolioProjectsDisplay from 'components/Shared/PortfolioProjectsDisplay/PortfolioProjectsDisplay';
import WorkExperienceDisplay from 'components/Shared/WorkExperienceDisplay/WorkExperienceDisplay';
import ButtonWithLoader from 'components/ButtonWithLoader/ButtonWithLoader';
import MessagesTab from 'components/ClientATS/MessagesTab';
import PaymentsTab from 'components/ClientATS/PaymentsTab';

import {
  addToTalentPool,
  removeFromTalentPool,
  isInTalentPool,
} from 'redux/modules/portfolio';
import { trackEvent } from 'helpers/Analytics';
import { beginsWithVowel } from 'helpers/HelperFunctions';
import styles from './ApplicantView.module.scss';
import InternalNotesTab from './InternalNotesTab';

// Action Button Components
const PrimaryActionButton = ({ action, disabled }) => {
  if (!action) return null;

  return (
    <ButtonWithLoader
      bsStyle="primary"
      onClick={action.onClick}
      loading={action.loading}
      disabled={disabled || action.disabled}
      icon={action.icon}
      text={action.loading ? action.loadingText || action.label : action.label}
    />
  );
};

PrimaryActionButton.propTypes = {
  action: PropTypes.shape({
    label: PropTypes.string.isRequired,
    loadingText: PropTypes.string,
    onClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
    icon: PropTypes.node,
  }),
  disabled: PropTypes.bool,
};

const SecondaryActionsDropdown = ({
  actions,
  showActionMenu,
  onToggle,
  disabled,
  showFavourite = false,
  favouriteAction,
}) => {
  if ((!actions || actions.length === 0) && !showFavourite) return null;

  return (
    <Dropdown
      id="secondary-actions-dropdown"
      pullRight
      open={showActionMenu}
      onToggle={onToggle}
    >
      <Dropdown.Toggle
        noCaret
        className={styles.actionMenuButton}
        disabled={disabled}
      >
        <Ellipsis width={24} height={24} />
      </Dropdown.Toggle>
      <Dropdown.Menu className={styles.actionMenuDropdown}>
        {actions.map((action, index) => (
          <MenuItem
            key={index}
            onClick={() => {
              action.onClick();
            }}
            disabled={action.disabled}
          >
            {action.icon}
            {action.loading ? action.loadingText : action.label}
          </MenuItem>
        ))}
        {showFavourite && favouriteAction && (
          <>
            <MenuItem divider />
            <MenuItem
              onClick={() => favouriteAction.onClick()}
              disabled={favouriteAction.disabled}
              className={styles.favouriteMenuItem}
              title={favouriteAction.tooltip}
            >
              {favouriteAction.icon}
              {favouriteAction.loading
                ? favouriteAction.loadingText
                : favouriteAction.label}
            </MenuItem>
          </>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

SecondaryActionsDropdown.propTypes = {
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      loadingText: PropTypes.string,
      onClick: PropTypes.func.isRequired,
      disabled: PropTypes.bool,
      loading: PropTypes.bool,
      icon: PropTypes.node,
    })
  ),
  showActionMenu: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  showFavourite: PropTypes.bool,
  favouriteAction: PropTypes.shape({
    label: PropTypes.string.isRequired,
    loadingText: PropTypes.string,
    onClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
    icon: PropTypes.node,
    tooltip: PropTypes.string,
  }),
};

const LeaveReviewButton = ({ ratingId, ratingUrlCode, briefEncodedId }) => {
  if (!ratingUrlCode || ratingUrlCode === false) {
    return null;
  }

  // Construct the review URL with redirect parameter to come back to ATS
  const reviewUrl = `/testimonials/${ratingId}/${ratingUrlCode}?redirect=/ats/${briefEncodedId}`;

  const handleClick = () => {
    window.location.href = reviewUrl;
  };

  return (
    <PrimaryActionButton
      action={{
        label: 'Leave Review',
        onClick: handleClick,
        icon: <Star width={20} height={20} className={styles.buttonIcon} />,
      }}
    />
  );
};

LeaveReviewButton.propTypes = {
  ratingId: PropTypes.number,
  ratingUrlCode: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  briefEncodedId: PropTypes.string,
};

const ApplicantView = ({
  pitch,
  user, // Normalized user object (from pitch.sender or applicant.user)
  applicantStage,
  onShortlist,
  onReview,
  onDecline,
  onHire,
  onEndHire,
  onReset,
  decliningPitch,
  shortlistingPitch,
  reviewingPitch,
  endingHire,
  resettingPitch,
  hiringPitch,
  currentUser,
  pitchMessages,
  loadingPitchMessages,
  markingThreadAsRead,
  markedThreads,
  onLoadMessages,
  onSendMessage,
  onSendFile,
  onMarkThreadAsRead,
  pitchMessagesMeta,
  onLoadMoreMessages,
  briefEncodedId,
  pitchPayments,
  pitchPaymentsStats,
  loadingPitchPayments,
  onLoadPayments,
  brief,
  internalNotes,
  loadingInternalNotes,
  onLoadInternalNotes,
  onLoadApplicantFeaturedProjects,
}) => {
  // Set initial tab based on applicant stage
  const [activeTab, setActiveTab] = useState(
    applicantStage === 'invited' ? 'portfolio' : 'application'
  );
  const [showActionMenu, setShowActionMenu] = useState(false);
  const tabsContainerRef = useRef(null);
  const prevApplicantIdRef = useRef(null);

  const username = user?.username;
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth?.user);

  const applicantFeaturedEntry = useSelector((state) =>
    username ? state.clientATS?.applicantFeaturedProjects?.[username] : null
  );
  const projectEntities = useSelector(
    (state) => state.entities?.projects || {}
  );

  const shortlistState = useSelector(
    (state) => username && state.portfolio && state.portfolio[username]
  );
  const isFavourited =
    shortlistState && typeof shortlistState.isInTalentPool !== 'undefined'
      ? shortlistState.isInTalentPool
      : false;
  const loadingFavourite =
    shortlistState && shortlistState.loadingShortlistState;

  useEffect(() => {
    if (!username || !authUser) return;
    if (
      !shortlistState ||
      (typeof shortlistState.isInTalentPool === 'undefined' &&
        !shortlistState.loadingShortlistState)
    ) {
      dispatch(isInTalentPool(username));
    }
  }, [authUser, dispatch, shortlistState, username]);

  const handleFavouriteToggle = useCallback(() => {
    if (!username || loadingFavourite) return;
    if (!authUser) {
      window.location = '/signin';
      return;
    }
    if (isFavourited) {
      dispatch(removeFromTalentPool(username));
    } else {
      trackEvent('user-shortlists-freelancer', {
        shortlist_type: 'client_ats',
      });
      dispatch(addToTalentPool(username));
    }
  }, [authUser, dispatch, isFavourited, loadingFavourite, username]);

  const featuredProjectsResolved = useMemo(() => {
    const ids = applicantFeaturedEntry?.projectIds;
    if (!Array.isArray(ids) || ids.length === 0) {
      return [];
    }
    return ids
      .map((projectId) => {
        const project = projectEntities[projectId];
        if (!project) return null;
        return { id: projectId, project, roles: [] };
      })
      .filter(Boolean);
  }, [applicantFeaturedEntry?.projectIds, projectEntities]);

  // Load featured projects when applicant is selected (pitched or invited)
  useEffect(() => {
    if (!username || !onLoadApplicantFeaturedProjects) return;
    if (applicantStage !== 'invited' && !pitch?.sender) return;

    if (applicantFeaturedEntry?.loaded || applicantFeaturedEntry?.loading) {
      return;
    }

    onLoadApplicantFeaturedProjects(username);
  }, [
    applicantStage,
    username,
    pitch?.sender,
    onLoadApplicantFeaturedProjects,
    applicantFeaturedEntry?.loaded,
    applicantFeaturedEntry?.loading,
  ]);

  // Reset to appropriate tab when user changes
  useEffect(() => {
    const currentId = pitch?.id || user?.id;

    if (currentId && currentId !== prevApplicantIdRef.current) {
      // For invited applicants, default to portfolio tab since application tab doesn't exist
      setActiveTab(applicantStage === 'invited' ? 'portfolio' : 'application');
      prevApplicantIdRef.current = currentId;
    }
  }, [pitch, user, applicantStage]);

  // Load internal notes when user is selected (not when tab is selected)
  useEffect(() => {
    if (pitch && pitch.sender && onLoadInternalNotes) {
      const freelancerId = pitch.sender.id;

      if (
        (!internalNotes || !internalNotes[freelancerId]) &&
        !loadingInternalNotes
      ) {
        onLoadInternalNotes(freelancerId);
      }
    }
  }, [pitch, onLoadInternalNotes, internalNotes, loadingInternalNotes]);

  // Load messages when user is selected (not when tab is selected)
  // Skip for invited users - they don't have messages or pitches
  useEffect(() => {
    if (applicantStage === 'invited') return;

    if (pitch && pitch.sender && onLoadMessages) {
      const username = pitch.sender.username;
      const pitchId = pitch.id;

      if (
        (!pitchMessages || !pitchMessages[pitchId]) &&
        !loadingPitchMessages
      ) {
        onLoadMessages(username, pitchId, false);
      }
    }
  }, [
    pitch,
    onLoadMessages,
    pitchMessages,
    loadingPitchMessages,
    applicantStage,
  ]);

  // Load payments when hired user is selected
  useEffect(() => {
    if (applicantStage !== 'hired') return;

    if (pitch && pitch.id && onLoadPayments) {
      const pitchId = pitch.id;
      // Load payments if they haven't been loaded for this pitch yet
      if (
        (!pitchPayments || !pitchPayments[pitchId]) &&
        !loadingPitchPayments
      ) {
        onLoadPayments(pitchId);
      }
    }
  }, [
    pitch,
    onLoadPayments,
    applicantStage,
    loadingPitchPayments,
    pitchPayments,
  ]);

  // Mark thread as read when messages tab is opened
  useEffect(() => {
    if (activeTab === 'messages' && pitch && pitch.sender) {
      const username = pitch.sender.username;
      const pitchId = pitch.id;

      // Check if there are unread messages
      const messages = pitchMessages && pitchMessages[pitchId];
      const hasUnreadMessages =
        messages &&
        messages.some(
          (msg) => msg.sender_id !== currentUser?.id && !msg.receiver_viewed
        );

      // Only mark as read if not already marking, not already marked, and has unread messages
      if (
        onMarkThreadAsRead &&
        !markingThreadAsRead &&
        !markedThreads[pitchId] &&
        hasUnreadMessages
      ) {
        onMarkThreadAsRead(username, pitchId);
      }
    }
  }, [
    activeTab,
    pitch?.id,
    markingThreadAsRead,
    markedThreads,
    pitchMessages,
    currentUser,
  ]);

  // Determine if this is an invited applicant (used in multiple places)
  const isInvited = applicantStage === 'invited';

  // User is now passed as a normalized prop from JobDetails
  if (!user) {
    return <div className={styles.emptyState}></div>;
  }

  // Get pitch ID from either pitch or user
  const pitchId = pitch?.id;

  // Check if there are unread messages
  // Skip for invited users - they don't have messages
  const hasUnreadMessages = isInvited
    ? false
    : !markedThreads[pitchId] && pitchMessages && pitchMessages[pitchId]
    ? pitchMessages[pitchId].some(
        (msg) => msg.sender_id !== currentUser?.id && !msg.receiver_viewed
      )
    : false;

  const tabs = [
    // Application and Messages tabs only show for non-invited applicants
    ...(!isInvited
      ? [
          { id: 'application', label: 'Application' },
          { id: 'messages', label: 'Messages', hasUnread: hasUnreadMessages },
        ]
      : []),
    { id: 'portfolio', label: 'Portfolio' },
    // Payments tab only shows for hired applicants
    ...(applicantStage === 'hired'
      ? [{ id: 'payments', label: 'Payments' }]
      : []),
    { id: 'internal', label: 'Internal Notes' },
  ];

  const handleShortlist = () => {
    if (onShortlist) {
      onShortlist();
    }
  };

  const handleReview = () => {
    if (onReview) {
      onReview();
    }
  };

  const handleDecline = () => {
    if (onDecline) {
      onDecline();
    }
  };

  const handleHire = () => {
    if (onHire) {
      onHire();
    }
  };

  const renderActionButtons = () => {
    const status = applicantStage || 'applied';
    const isAnyActionLoading =
      decliningPitch || shortlistingPitch || resettingPitch || hiringPitch;

    // Define action configurations with icons
    const shortlistAction = {
      label: 'Move to Shortlisted',
      loadingText: 'Moving...',
      onClick: handleShortlist,
      disabled: shortlistingPitch,
      loading: shortlistingPitch,
      icon: <Star width={20} height={20} className={styles.buttonIcon} />,
    };

    const hireAction = {
      label: 'Move to Hired',
      loadingText: 'Moving...',
      onClick: handleHire,
      disabled: hiringPitch,
      loading: hiringPitch,
      icon: <Check width={20} height={20} className={styles.buttonIcon} />,
    };

    const moveToAppliedAction = {
      label: 'Move to Applied',
      loadingText: 'Moving...',
      onClick: onReset,
      disabled: resettingPitch,
      loading: resettingPitch,
      icon: <ArrowLeft width={16} height={16} />,
    };

    const reviewAction = {
      label: 'Move to Reviewed',
      loadingText: 'Moving...',
      onClick: handleReview,
      disabled: reviewingPitch,
      loading: reviewingPitch,
      icon: <ClipboardCheck width={20} height={20} />,
    };

    const moveToReviewedAction = {
      label: 'Move to Reviewed',
      loadingText: 'Moving...',
      onClick: () => onReset(false),
      disabled: resettingPitch,
      loading: resettingPitch,
      icon: <ArrowLeft width={16} height={16} />,
    };

    const moveToReviewedDeclinedAction = {
      label: 'Move to Reviewed',
      loadingText: 'Moving...',
      onClick: () => onReset(false),
      disabled: resettingPitch,
      loading: resettingPitch,
      icon: <ArrowRight width={16} height={16} />,
    };

    const endHireAction = {
      label: 'End Hire',
      loadingText: 'Ending...',
      onClick: onEndHire,
      disabled: endingHire,
      loading: endingHire,
      icon: <CheckLine width={16} height={16} />,
    };

    const declineAction = {
      label: 'Not a fit',
      loadingText: 'Moving...',
      onClick: handleDecline,
      disabled: decliningPitch,
      loading: decliningPitch,
      icon: <X width={16} height={16} />,
    };

    const hireSecondaryAction = {
      ...hireAction,
      icon: <Check width={16} height={16} />,
    };

    const shortlistSecondaryAction = {
      ...shortlistAction,
      icon: <Star width={16} height={16} />,
    };

    const favouriteAction = {
      label: isFavourited ? 'Remove from Talent Pool' : 'Add to Talent Pool',
      loadingText: 'Updating...',
      onClick: handleFavouriteToggle,
      disabled: loadingFavourite || !username,
      loading: loadingFavourite,
      icon: isFavourited ? (
        <HeartMinus width={16} height={16} />
      ) : (
        <Heart width={16} height={16} />
      ),
      tooltip: isFavourited
        ? 'Remove this expert from your Talent Pool'
        : 'Add this expert to your Talent Pool so you can easily find them later',
    };

    switch (status) {
      case 'applied':
        return (
          <div className={styles.actionButtons}>
            <PrimaryActionButton
              action={reviewAction}
              disabled={isAnyActionLoading}
            />
            <SecondaryActionsDropdown
              actions={[
                shortlistSecondaryAction,
                hireSecondaryAction,
                declineAction,
              ]}
              showActionMenu={showActionMenu}
              onToggle={(isOpen) => setShowActionMenu(isOpen)}
              disabled={isAnyActionLoading}
              showFavourite
              favouriteAction={favouriteAction}
            />
          </div>
        );
      case 'reviewed':
        return (
          <div className={styles.actionButtons}>
            <PrimaryActionButton
              action={shortlistAction}
              disabled={isAnyActionLoading}
            />
            <SecondaryActionsDropdown
              actions={[
                moveToAppliedAction,
                hireSecondaryAction,
                declineAction,
              ]}
              showActionMenu={showActionMenu}
              onToggle={(isOpen) => setShowActionMenu(isOpen)}
              disabled={isAnyActionLoading}
              showFavourite
              favouriteAction={favouriteAction}
            />
          </div>
        );
      case 'shortlisted':
        return (
          <div className={styles.actionButtons}>
            <PrimaryActionButton
              action={hireAction}
              disabled={isAnyActionLoading}
            />
            <SecondaryActionsDropdown
              actions={[
                moveToAppliedAction,
                moveToReviewedAction,
                declineAction,
              ]}
              showActionMenu={showActionMenu}
              onToggle={(isOpen) => setShowActionMenu(isOpen)}
              disabled={isAnyActionLoading}
              showFavourite
              favouriteAction={favouriteAction}
            />
          </div>
        );
      case 'declined':
        return (
          <div className={styles.actionButtons}>
            <SecondaryActionsDropdown
              actions={[moveToAppliedAction, moveToReviewedDeclinedAction]}
              showActionMenu={showActionMenu}
              onToggle={(isOpen) => setShowActionMenu(isOpen)}
              disabled={isAnyActionLoading}
              showFavourite
              favouriteAction={favouriteAction}
            />
          </div>
        );
      case 'hired':
        return (
          <div className={styles.actionButtons}>
            <LeaveReviewButton
              ratingId={pitch?.rating?.id}
              ratingUrlCode={pitch?.rating?.url_code}
              briefEncodedId={briefEncodedId}
            />
            {!pitch?.completed_at && (
              <SecondaryActionsDropdown
                actions={[endHireAction]}
                showActionMenu={showActionMenu}
                onToggle={(isOpen) => setShowActionMenu(isOpen)}
                disabled={isAnyActionLoading}
                showFavourite
                favouriteAction={favouriteAction}
              />
            )}
          </div>
        );
      default:
        return null;
    }
  };

  const generateGenericBio = () => {
    const username = user?.display_name ?? user?.username;
    const role = user?.featured_roles[0] ?? '';
    const location = user?.location?.text ?? '';

    let genericBio = ``;

    if (role && location) {
      const article = beginsWithVowel(role) ? 'an' : 'a';
      genericBio += `${username} is ${article} ${role} based in ${location}.`;
    } else if (role) {
      const article = beginsWithVowel(role) ? 'an' : 'a';
      genericBio += `${username} is ${article} ${role}.`;
    } else if (location) {
      genericBio += `${username} is a freelancer based in ${location}.`;
    }

    return genericBio;
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'application':
        return (
          <div className={styles.applicationTab}>
            <div className={styles.card}>
              <h4 className={styles.cardTitle}>Pitch</h4>
              <div className={styles.pitchMessage}>
                {pitch?.text ? (
                  <Markdown text={pitch.text} target="_blank" />
                ) : (
                  <p>No pitch message available</p>
                )}
              </div>
            </div>
            {user?.resume?.url && (
              <div className={styles.card}>
                <h4 className={styles.cardTitle}>Resume</h4>
                <div className={styles.resumeContainer}>
                  {user.resume.mime_type === 'application/pdf' ? (
                    <>
                      <object
                        data={`${user.resume.url}#navpanes=0&view=FitH`}
                        type="application/pdf"
                        className={styles.resumeEmbed}
                      >
                        <p>
                          {"Your browser can't display PDFs. "}
                          <a
                            href={user.resume.url}
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Download the PDF
                          </a>
                          .
                        </p>
                      </object>
                      <div className={styles.resumeDownloadLink}>
                        <a
                          href={user.resume.url}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Download
                            width={16}
                            height={16}
                            style={{ marginRight: '8px' }}
                          />
                          Download Resume
                        </a>
                      </div>
                    </>
                  ) : (
                    <div className={styles.resumeDownloadOnly}>
                      <p>Resume preview not available for this file type.</p>
                      <a
                        href={user.resume.url}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.resumeDownloadButton}
                      >
                        <Download
                          width={16}
                          height={16}
                          style={{ marginRight: '8px' }}
                        />
                        Download Resume
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      case 'portfolio': {
        const genericBioText = generateGenericBio();
        return (
          <div className={styles.portfolioTab}>
            <div className={styles.card}>
              <h4 className={styles.cardTitle}>Bio</h4>
              <div className={styles.pitchMessage}>
                {user?.bio ? (
                  <Markdown text={user.bio} target="_blank" />
                ) : (
                  <>
                    {genericBioText && <p>{genericBioText}</p>}
                    <p>
                      To learn more details about this expert view their
                      portfolio
                    </p>
                  </>
                )}
              </div>
            </div>
            {user?.workexperience?.length > 0 ? (
              <WorkExperienceDisplay items={user.workexperience} />
            ) : null}
            <PortfolioProjectsDisplay
              items={featuredProjectsResolved}
              user={user}
              loading={
                !!applicantFeaturedEntry?.loading &&
                featuredProjectsResolved.length === 0
              }
            />
            <div className={styles.portfolioButtonContainer}>
              <Button
                bsStyle="primary"
                bsSize="large"
                className={styles.portfolioButton}
                href={user?.links?.main_relative}
                target="_blank"
              >
                View Portfolio
              </Button>
            </div>
          </div>
        );
      }
      case 'messages':
        return (
          <MessagesTab
            messages={
              pitchMessages && pitchMessages[pitch.id]
                ? pitchMessages[pitch.id]
                : []
            }
            loading={loadingPitchMessages}
            currentUser={currentUser}
            otherUser={user}
            onSendMessage={onSendMessage}
            onSendFile={onSendFile}
            hasMore={
              pitchMessagesMeta && pitchMessagesMeta[pitch.id]
                ? pitchMessagesMeta[pitch.id].more
                : false
            }
            onLoadMore={() =>
              onLoadMoreMessages && onLoadMoreMessages(pitch.id)
            }
          />
        );
      case 'internal':
        return (
          <InternalNotesTab
            internalNotes={
              internalNotes && internalNotes[user.id]
                ? internalNotes[user.id]
                : []
            }
            freelancer={user}
          />
        );
      case 'payments':
        return (
          <PaymentsTab
            payments={
              pitchPayments && pitchPayments[pitch.id]
                ? pitchPayments[pitch.id]
                : []
            }
            stats={
              pitchPaymentsStats && pitchPaymentsStats[pitch.id]
                ? pitchPaymentsStats[pitch.id]
                : null
            }
            freelancerName={user?.displayname || user?.username}
            loading={loadingPitchPayments}
            brief={brief}
            pitch={pitch}
            onLoadPayments={onLoadPayments}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <ApplicantSummaryCard
          user={user}
          actionButtons={renderActionButtons()}
        />
      </div>

      <div className={styles.tabsContainer} ref={tabsContainerRef}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              if (tab.id === 'portfolio') {
                trackEvent('client-views-portfolio-tab');
              } else if (tab.id === 'messages') {
                trackEvent('client-views-messages-tab');
              } else if (tab.id === 'internal') {
                trackEvent('client-views-internal-note-tab');
              }
              // Scroll tab into view on mobile
              if (window.innerWidth <= 768) {
                setTimeout(() => {
                  const button = tabsContainerRef.current?.querySelector(
                    `.${styles.tabActive}`
                  );
                  if (button) {
                    button.scrollIntoView({
                      behavior: 'smooth',
                      block: 'nearest',
                      inline: 'center',
                    });
                  }
                }, 100);
              }
              // Scroll to bottom when Messages tab is selected on mobile
              if (tab.id === 'messages') {
                setTimeout(() => {
                  window.scrollTo({
                    top: document.documentElement.scrollHeight,
                    behavior: 'smooth',
                  });
                }, 150);
              }
            }}
            className={`${styles.tab} ${
              activeTab === tab.id ? styles.tabActive : ''
            }`}
          >
            {tab.label}
            {tab.hasUnread && <span className={styles.tabUnreadDot} />}
          </button>
        ))}
      </div>

      <div className={styles.tabContent}>{renderTabContent()}</div>
    </div>
  );
};

ApplicantView.propTypes = {
  pitch: PropTypes.shape({
    id: PropTypes.number,
    text: PropTypes.string,
    sender: PropTypes.object,
    timestamp: PropTypes.string,
    completed_at: PropTypes.string,
    rating: PropTypes.shape({
      id: PropTypes.number,
      url_code: PropTypes.string,
    }),
  }),
  user: PropTypes.shape({
    id: PropTypes.number,
    display_name: PropTypes.string,
    username: PropTypes.string,
    displayname: PropTypes.string,
    bio: PropTypes.string,
    location: PropTypes.object,
    featured_roles: PropTypes.array,
    links: PropTypes.object,
    workexperience: PropTypes.array,
    resume: PropTypes.shape({
      url: PropTypes.string,
      mime_type: PropTypes.string,
    }),
  }), // Normalized user object from pitch.sender or applicant.user
  applicantStage: PropTypes.oneOf([
    'applied',
    'reviewed',
    'shortlisted',
    'declined',
    'hired',
    'invited',
  ]),
  onShortlist: PropTypes.func,
  onReview: PropTypes.func,
  onDecline: PropTypes.func,
  onHire: PropTypes.func,
  onEndHire: PropTypes.func,
  onReset: PropTypes.func,
  decliningPitch: PropTypes.bool,
  shortlistingPitch: PropTypes.bool,
  reviewingPitch: PropTypes.bool,
  endingHire: PropTypes.bool,
  resettingPitch: PropTypes.bool,
  hiringPitch: PropTypes.bool,
  currentUser: PropTypes.object,
  pitchMessages: PropTypes.object,
  loadingPitchMessages: PropTypes.bool,
  markingThreadAsRead: PropTypes.bool,
  markedThreads: PropTypes.object,
  onLoadMessages: PropTypes.func,
  onSendMessage: PropTypes.func,
  onSendFile: PropTypes.func,
  onMarkThreadAsRead: PropTypes.func,
  pitchMessagesMeta: PropTypes.object,
  onLoadMoreMessages: PropTypes.func,
  briefEncodedId: PropTypes.string,
  pitchPayments: PropTypes.object, // Keyed by pitchId
  pitchPaymentsStats: PropTypes.object, // Keyed by pitchId
  loadingPitchPayments: PropTypes.bool,
  onLoadPayments: PropTypes.func,
  brief: PropTypes.object,
  internalNotes: PropTypes.object,
  loadingInternalNotes: PropTypes.bool,
  onLoadInternalNotes: PropTypes.func,
  onLoadApplicantFeaturedProjects: PropTypes.func,
};

export default ApplicantView;
